import * as THREE from 'three';
import { Road } from './perspective-road.js';
import { Road as RoadAlt } from './perspective-road-alt.js';
import { Mountains } from './mountains.js';
import Stats from 'three/examples/jsm/libs/stats.module.js';
import { UnrealBloomPass } from 'three/examples/jsm/postprocessing/UnrealBloomPass.js';
import { detectGPU, isWebGLAvailable } from './utils/gpu-detection.js';
import { setupWebGLPostProcessing, setupWebGPUPostProcessing, updateFXAAResolution } from './utils/post-processing.js';
import { AdaptiveQuality } from './utils/adaptive-quality.js';
import { defaultConfig } from './config/default-config.js';
import { mergeDeep } from './utils/config.js';

/**
 * Klasa zarządzająca sceną WebGL w stylu retro synth
 */
export class SynthScene {
    constructor(container, config = {}) {
        this.container = container;
        this.config = this.mergeConfig(config);
        this.scene = null;
        this.camera = null;
        this.renderer = null;
        this.composer = null; // EffectComposer dla post-processing (WebGL)
        this.postProcessing = null; // PostProcessing dla WebGPU
        this.bloomPass = null; // Bloom pass dla WebGPU
        this.scenePass = null; // Scene pass dla WebGPU (TSL)
        this.scenePassColor = null; // Scene pass color node dla WebGPU (TSL)
        this.bloomModule = null; // Bloom module dla WebGPU (zapisany do użycia w updateConfig)
        this.animationId = null;
        this.sun = null;
        this.road = null;
        this.stats = null;
        
        // Adaptive quality
        this.adaptiveQuality = new AdaptiveQuality({
            enabled: true,
            targetFPS: 50,
            minFPS: 30,
            checkInterval: 60,
            fpsHistorySize: 10,
            stepSize: 0.1
        });
    }

    /**
     * Merge konfiguracji z domyślną
     */
    mergeConfig(config) {
        return mergeDeep({}, defaultConfig, config);
    }

    /**
     * Inicjalizacja sceny Three.js
     */
    async init() {
        if (!this.container) {
            console.error('Container not found');
            return false;
        }

        // Sprawdzenie WebGL
        if (!isWebGLAvailable()) {
            console.warn('WebGL not available');
            return false;
        }

        // Utworzenie sceny
        this.scene = new THREE.Scene();
        this.scene.background = new THREE.Color(0x000000);
        this.scene.fog = new THREE.Fog(0x000000, 50, 500);

        // Utworzenie kamery - horyzont na środku ekranu
        // Użyj visualViewport jeśli dostępne (lepsze dla mobile), w przeciwnym razie window
        const viewportWidth = window.visualViewport?.width || window.innerWidth;
        const viewportHeight = window.visualViewport?.height || window.innerHeight;
        const aspect = viewportWidth / viewportHeight;
        this.camera = new THREE.PerspectiveCamera(
            this.config.camera.fov,
            aspect,
            0.1,
            1000
        );
        // Kamera patrzy w kierunku ujemnych Z (gdzie jest droga)
        // Ustawiamy kamerę już nad drogą - droga zaczyna się w -50, więc ustawiamy kamerę w -30
        this.cameraZ = -30; // Pozycja Z kamery w przestrzeni świata - już nad drogą
        this.camera.position.set(0, 0, this.cameraZ);
        this.camera.lookAt(0, 0, -100); // Patrzy w kierunku drogi (dostosowane do nowego startZ)

        // Utworzenie renderera
        const bloomConfig = this.config.ground?.bloom || {};
        const usePostProcessing = bloomConfig.enabled;
        
        // Wykryj możliwości GPU
        const gpuInfo = detectGPU();
        console.log('[GPU Info]', gpuInfo);
        
        // WebGPU detection (async)
        this.webGPUAvailable = false;
        
        // Konfiguracja renderera z optymalizacjami dla GPU
        // Dla Basic Render Driver próbuj wymusić hardware acceleration
        const forceHardware = gpuInfo.isSoftwareRenderer && gpuInfo.renderer.includes('Basic Render Driver');
        
        const rendererOptions = {
            antialias: !gpuInfo.isSoftwareRenderer || forceHardware, // Spróbuj włączyć dla Basic Render Driver
            alpha: true,
            // Wymuś high-performance nawet dla Basic Render Driver - może to pomoże przeglądarce użyć GPU
            powerPreference: forceHardware ? 'high-performance' : (gpuInfo.hasDedicatedGPU && !gpuInfo.isSoftwareRenderer ? 'high-performance' : 'default'),
            precision: (gpuInfo.isHighEnd && !gpuInfo.isSoftwareRenderer) ? 'highp' : (forceHardware ? 'mediump' : 'lowp'),
            stencil: false, // Wyłącz jeśli nie potrzebne (lepsza wydajność)
            depth: true,
            logarithmicDepthBuffer: false // Wyłącz dla lepszej wydajności (chyba że potrzebne)
        };
        
        // Spróbuj użyć WebGPU jeśli dostępne (znacznie szybsze niż WebGL - jak u Bruno Simona)
        // Zgodnie z zaleceniami Chrome: https://developer.chrome.com/docs/web-platform/webgpu/from-webgl-to-webgpu
        let useWebGPU = false;
        let webGPUAdapter = null;
        
        if (typeof navigator !== 'undefined' && navigator.gpu) {
            try {
                // WebGPU jest asynchroniczne - używamy await zgodnie z zaleceniami
                // powerPreference: 'high-performance' dla dedykowanego GPU
                webGPUAdapter = await navigator.gpu.requestAdapter({ 
                    powerPreference: gpuInfo.hasDedicatedGPU ? 'high-performance' : 'default'
                });
                
                if (webGPUAdapter) {
                    useWebGPU = true;
                    // Sprawdź limity WebGPU (zgodnie z dokumentacją Chrome)
                    const limits = webGPUAdapter.limits;
                    console.log('[Renderer] ⚡ WebGPU adapter dostępny - próbuję użyć WebGPU renderer');
                    console.log('[Renderer] WebGPU limits:', {
                        maxTextureSize: limits.maxTextureDimension2D,
                        maxStorageBufferSize: limits.maxStorageBufferBindingSize,
                        maxUniformBufferSize: limits.maxUniformBufferBindingSize
                    });
                }
            } catch (e) {
                console.log('[Renderer] WebGPU niedostępne, używam WebGL:', e.message);
            }
        }
        
        // Spróbuj użyć WebGL2 jeśli dostępne (lepsze możliwości GPU)
        if (!useWebGPU && gpuInfo.webgl2Supported && (!gpuInfo.isSoftwareRenderer || forceHardware)) {
            rendererOptions.powerPreference = 'high-performance';
            console.log('[Renderer] Używam WebGL2 dla lepszej wydajności GPU');
        }
        
        // Sprawdź czy post-processing (bloom) jest włączony
        // WebGPU używa THREE.PostProcessing z TSL, WebGL używa EffectComposer
        // (używamy już zadeklarowanej zmiennej usePostProcessing z linii 147)
        
        // Użyj WebGPU renderer jeśli dostępne (możemy użyć bloom nawet z WebGPU przez TSL)
        if (useWebGPU) {
            try {
                // Dynamiczny import WebGPU - tylko gdy dostępne (zapobiega błędom na urządzeniach bez WebGPU)
                const webGPUModule = await import('three/webgpu');
                const { WebGPURenderer } = webGPUModule;
                
                if (WebGPURenderer) {
                    // WebGPURenderer z Three.js r160+ - automatycznie fallback do WebGL jeśli WebGPU nie jest dostępne
                    this.renderer = new WebGPURenderer(rendererOptions);
                    // WebGPURenderer wymaga asynchronicznej inicjalizacji
                    await this.renderer.init();
                    console.log('[Renderer] ⚡ WebGPU renderer aktywowany i zainicjalizowany - znacznie szybszy niż WebGL!');
                } else {
                    throw new Error('WebGPURenderer nie jest dostępny w module');
                }
            } catch (e) {
                console.log('[Renderer] Błąd inicjalizacji WebGPU renderer, używam WebGL:', e.message);
                useWebGPU = false;
                this.renderer = new THREE.WebGLRenderer(rendererOptions);
            }
        } else {
            this.renderer = new THREE.WebGLRenderer(rendererOptions);
        }
        
        // Zapisz informację o użyciu WebGPU dla późniejszego użycia
        this.useWebGPU = useWebGPU;
        this.webGPUAdapter = webGPUAdapter;
        
        // Optymalizacje renderera dla GPU
        if (gpuInfo.isHighEnd && !gpuInfo.isSoftwareRenderer) {
            // Dla mocnych GPU - wyższa jakość
            this.renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2.0));
        } else if (gpuInfo.isSoftwareRenderer && !forceHardware) {
            // Dla software renderera - bardzo niska jakość
            this.renderer.setPixelRatio(1.0);
            console.warn('[Renderer] ⚠️ Software renderer wykryty - używam minimalnych ustawień wydajności');
        } else if (forceHardware) {
            // Dla Basic Render Driver - spróbuj średnich ustawień (może przeglądarka użyje GPU)
            this.renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 1.5));
            console.log('[Renderer] Basic Render Driver wykryty - próbuję wymusić hardware acceleration');
        }
        // Konfiguracja rozdzielczości dla wydajności
        const renderConfig = this.config.render || {};
        const baseResolutionScale = renderConfig.resolutionScale || 1.0;
        
        // Ustaw bazową wartość dla adaptive quality
        if (this.adaptiveQuality.enabled) {
            const isBasicRenderDriver = gpuInfo.isSoftwareRenderer && gpuInfo.renderer.includes('Basic Render Driver');
            
            if (gpuInfo.isSoftwareRenderer && !isBasicRenderDriver) {
                this.adaptiveQuality.baseResolutionScale = Math.min(baseResolutionScale, 0.3);
                this.adaptiveQuality.currentResolutionScale = 0.3;
                this.adaptiveQuality.minFPS = 20;
                console.warn('[Adaptive Quality] Software renderer - startuję od niskiej jakości (0.3)');
            } else if (isBasicRenderDriver) {
                this.adaptiveQuality.baseResolutionScale = Math.min(baseResolutionScale, 0.5);
                this.adaptiveQuality.currentResolutionScale = 0.5;
                this.adaptiveQuality.minFPS = 25;
                console.log('[Adaptive Quality] Basic Render Driver - startuję od średniej jakości (0.5), próbuję wymusić GPU');
            } else {
                this.adaptiveQuality.baseResolutionScale = baseResolutionScale;
                this.adaptiveQuality.currentResolutionScale = baseResolutionScale;
            }
        }
        
        const currentResolutionScale = this.adaptiveQuality.enabled 
            ? this.adaptiveQuality.currentResolutionScale
            : baseResolutionScale;
        
        const devicePixelRatio = window.devicePixelRatio || 1.0;
        const targetPixelRatio = renderConfig.pixelRatio !== null && renderConfig.pixelRatio !== undefined
            ? renderConfig.pixelRatio 
            : Math.min(devicePixelRatio, 2.0); // Ograniczenie do max 2.0 dla wydajności
        
        // Oblicz docelową rozdzielczość (używamy viewportWidth i viewportHeight zadeklarowanych wcześniej)
        const targetWidth = Math.floor(viewportWidth * currentResolutionScale);
        const targetHeight = Math.floor(viewportHeight * currentResolutionScale);
        
        this.renderer.setSize(targetWidth, targetHeight);
        this.renderer.setPixelRatio(targetPixelRatio);
        
        // Konfiguracja HDR tylko gdy używamy post-processing (bloom)
        if (usePostProcessing) {
            this.renderer.toneMapping = THREE.ACESFilmicToneMapping; // Tone mapping dla HDR
            this.renderer.toneMappingExposure = 1.0; // Ekspozycja
            this.renderer.autoClear = false; // Wyłącz auto clear dla EffectComposer
        }
        
        // Ustawienie clearColor na czarny (zamiast przezroczystego) dla stabilności
        this.renderer.setClearColor(0x000000, 0); // Czarny z pełną przezroczystością (alpha: 0)
        
        // Logowanie rozdzielczości dla debugowania
        const actualWidth = targetWidth * targetPixelRatio;
        const actualHeight = targetHeight * targetPixelRatio;
        console.log(`Render resolution: ${targetWidth}x${targetHeight} @ ${targetPixelRatio}x = ${actualWidth}x${actualHeight}px (scale: ${currentResolutionScale}, viewport: ${viewportWidth}x${viewportHeight}, window: ${window.innerWidth}x${window.innerHeight}, adaptive: ${this.adaptiveQuality.enabled ? 'ON' : 'OFF'})`);
        
        // Ustawienie canvas w kontenerze
        const canvas = this.renderer.domElement;
        canvas.style.position = 'fixed';
        canvas.style.top = '0';
        canvas.style.left = '0';
        canvas.style.width = `${viewportWidth}px`;
        canvas.style.height = `${viewportHeight}px`;
        canvas.style.zIndex = '-2';
        canvas.style.pointerEvents = 'none';
        
        this.container.appendChild(canvas);

        // Utworzenie post-processing (bloom effect) - async dla WebGPU
        await this.setupPostProcessing();

        // Utworzenie elementów sceny
        this.createRoad();
        this.createMountains();
        this.createSun();
        window.__synthScene = this;

        // Stats - monitorowanie wydajności
        this.stats = new Stats();
        this.stats.dom.style.position = 'fixed';
        this.stats.dom.style.top = '10px';
        this.stats.dom.style.left = '10px';
        this.stats.dom.style.zIndex = '1000';
        document.body.appendChild(this.stats.dom);
        
        // Event listeners
        window.addEventListener('resize', () => this.onWindowResize());
        // VisualViewport API dla lepszej obsługi mobile (zmiana wysokości przy pokazywaniu/ukrywaniu paska adresu)
        if (window.visualViewport) {
            window.visualViewport.addEventListener('resize', () => this.onWindowResize());
        }

        // Start animacji
        this.animate();

        return true;
    }
    
    /**
     * Wykryj możliwości GPU i dostępność WebGL
     */
    detectGPU() {
        const info = {
            webglSupported: false,
            webgl2Supported: false,
            hasDedicatedGPU: false,
            isHighEnd: false,
            isSoftwareRenderer: false, // Software renderer (bardzo wolny)
            renderer: 'unknown',
            vendor: 'unknown',
            maxTextureSize: 0,
            maxVertexAttribs: 0
        };
        
        // Sprawdź WebGL1
        const canvas = document.createElement('canvas');
        const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
        
        if (!gl) {
            console.warn('[GPU] WebGL nie jest dostępne!');
            return info;
        }
        
        info.webglSupported = true;
        
        // Pobierz informacje o rendererze (GPU)
        const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
        if (debugInfo) {
            info.renderer = gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL) || 'unknown';
            info.vendor = gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL) || 'unknown';
            
            const rendererLower = info.renderer.toLowerCase();
            
            // Wykryj software renderer (bardzo wolny - użyj niskich ustawień)
            // UWAGA: ANGLE (Microsoft Basic Render Driver) może oznaczać że przeglądarka używa software renderera
            // zamiast dedykowanego GPU, nawet jeśli GPU jest dostępne
            const isBasicRenderDriver = (
                rendererLower.includes('basic render driver') ||
                rendererLower.includes('microsoft basic render driver')
            );
            
            const isDefinitelySoftware = (
                rendererLower.includes('software') ||
                rendererLower.includes('llvmpipe') ||
                rendererLower.includes('mesa software')
            );
            
            // Jeśli to Basic Render Driver, sprawdź czy może być to problem z konfiguracją
            if (isBasicRenderDriver) {
                console.warn('[GPU] ⚠️ Wykryto "Basic Render Driver" - przeglądarka może używać software renderera zamiast GPU!');
                console.warn('[GPU] Jeśli masz dedykowane GPU (np. NVIDIA), sprawdź:');
                console.warn('[GPU] 1. Ustawienia przeglądarki: chrome://settings/system → "Użyj akceleracji sprzętowej"');
                console.warn('[GPU] 2. Sterowniki NVIDIA: Panel sterowania NVIDIA → Zarządzanie ustawieniami 3D → Programy');
                console.warn('[GPU] 3. Windows Graphics Settings: Ustawienia → System → Wyświetlanie → Graphics settings');
                
                // Traktuj jako software renderer, ale z ostrzeżeniem
                info.isSoftwareRenderer = true;
            } else if (isDefinitelySoftware) {
                info.isSoftwareRenderer = true;
                console.warn('[GPU] ⚠️ Wykryto software renderer - wydajność będzie bardzo niska!');
            }
            
            // Wykryj dedykowane GPU (zwykle zawiera "NVIDIA", "AMD", "Intel" dla dedykowanych)
            info.hasDedicatedGPU = (
                rendererLower.includes('nvidia') ||
                rendererLower.includes('amd') ||
                rendererLower.includes('radeon') ||
                rendererLower.includes('geforce') ||
                rendererLower.includes('rtx') ||
                rendererLower.includes('gtx')
            ) && !rendererLower.includes('intel');
            
            // Wykryj wysokiej klasy GPU
            info.isHighEnd = (
                rendererLower.includes('rtx') ||
                rendererLower.includes('geforce rtx') ||
                rendererLower.includes('radeon rx') ||
                rendererLower.includes('amd radeon rx') ||
                rendererLower.includes('geforce gtx 10') ||
                rendererLower.includes('geforce gtx 16') ||
                rendererLower.includes('geforce gtx 20') ||
                rendererLower.includes('geforce gtx 30') ||
                rendererLower.includes('geforce gtx 40')
            );
        }
        
        // Sprawdź WebGL2
        const gl2 = canvas.getContext('webgl2');
        if (gl2) {
            info.webgl2Supported = true;
        }
        
        // Pobierz limity GPU
        info.maxTextureSize = gl.getParameter(gl.MAX_TEXTURE_SIZE) || 0;
        info.maxVertexAttribs = gl.getParameter(gl.MAX_VERTEX_ATTRIBS) || 0;
        
        // Dodatkowe sprawdzenie - jeśli maxTextureSize jest duże i to nie software renderer
        if (!info.isHighEnd && !info.isSoftwareRenderer && info.maxTextureSize >= 16384) {
            // Ale tylko jeśli to nie Intel zintegrowane (zwykle słabsze)
            const rendererLower = info.renderer.toLowerCase();
            if (!rendererLower.includes('intel') || rendererLower.includes('iris') || rendererLower.includes('xe')) {
                info.isHighEnd = true;
            }
        }
        
        return info;
    }

    /**
     * Konfiguracja post-processing (bloom effect)
     */
    async setupPostProcessing() {
        const bloomConfig = this.config.ground.bloom || {};
        
        if (!bloomConfig.enabled) {
            return;
        }
        
        const isWebGPURenderer = this.useWebGPU && this.renderer && 
            this.renderer.constructor.name === 'WebGPURenderer';
        
        if (isWebGPURenderer) {
            const result = await setupWebGPUPostProcessing(
                this.renderer, 
                this.scene, 
                this.camera, 
                bloomConfig
            );
            if (result) {
                this.postProcessing = result.postProcessing;
                this.scenePass = result.scenePass;
                this.scenePassColor = result.scenePassColor;
                this.bloomModule = result.bloomModule;
            }
        } else {
            this.composer = setupWebGLPostProcessing(
                this.renderer,
                this.scene,
                this.camera,
                bloomConfig,
                this.config.render || {}
            );
        }
    }

    /**
     * Utworzenie drogi z dynamicznym generowaniem punktów
     * Używa prostszej wersji (EdgesGeometry) jeśli useAltRoadRenderer = true
     */
    createRoad() {
        const useAltRenderer = this.config.ground?.useAltRoadRenderer || false;
        const RoadClass = useAltRenderer ? RoadAlt : Road;
        
        this.road = new RoadClass({
            linesCount: this.config.ground.linesCount || 100,
            startZ: -50, // Przesunięte do przodu (bliżej kamery) - było -200
            roadWidth: 20,
            segmentSpacing: 6,
            cameraSpeed: this.config.camera.speed,
            lineWidth: this.config.ground.lineWidth,
            glowIntensity: this.config.ground.glowIntensity,
            useThickLines: !useAltRenderer, // Tylko dla starej wersji
            lineBreaks: this.config.ground.lineBreaks
        });
        
        const roadGroup = this.road.init();
        this.scene.add(roadGroup);
        
        // Ustaw rozdzielczość dla LineMaterial (wymagane dla Line2)
        if (useAltRenderer && this.road.setResolution) {
            const viewportWidth = window.visualViewport?.width || window.innerWidth;
            const viewportHeight = window.visualViewport?.height || window.innerHeight;
            this.road.setResolution(viewportWidth, viewportHeight);
        }
        
        console.log(`[Road] Używam ${useAltRenderer ? 'prostszej wersji (Line2)' : 'starej wersji (TubeGeometry)'}`);
    }

    /**
     * Utworzenie gór po bokach drogi (wireframe + nieprzezroczysty fill blokujący słońce).
     */
    createMountains() {
        const cfg = this.config.mountains || {};
        this.mountains = new Mountains({
            enabled: cfg.enabled !== false,
            solid: cfg.solid !== false,
            fillColor: cfg.fillColor ?? 0x000000,
            roadWidth: 20,
            stripWidth: cfg.stripWidth || 80,
            segmentSpacing: 6,
            rows: cfg.rows || 30,
            cols: cfg.cols || 12,
            height: cfg.height || 40,
            noiseScale: cfg.noiseScale || 0.04,
            lineWidth: this.config.ground.lineWidth,
            glowIntensity: this.config.ground.glowIntensity,
            baseColor: 0x15F8E5,
            baseLineY: -5
        });
        this.scene.add(this.mountains.group);
    }


    /**
     * Utworzenie słońca na horyzoncie
     */
    createSun() {
        const size = this.config.sun.size;
        
        // Główna kula słońca
        // MeshStandardMaterial obsługuje emissive i emissiveIntensity (MeshBasicMaterial nie)
        const geometry = new THREE.SphereGeometry(size, 32, 32);
        const material = new THREE.MeshStandardMaterial({
            color: this.config.sun.color,
            emissive: this.config.sun.color,
            emissiveIntensity: 1.5,
            depthWrite: true,
            depthTest: true,
            metalness: 0.0, // Nie metaliczny
            roughness: 1.0 // Maksymalna szorstkość (matowy)
        });
        
        this.sun = new THREE.Mesh(geometry, material);
        this.sun.renderOrder = 0; // Słońce renderowane wcześniej (za drogą)
        
        // Pozycja słońca - względna do kamery (zawsze w tym samym miejscu na ekranie)
        // Słońce będzie w stałej odległości przed kamerą
        const sunDistance = this.config.ground.startZ || this.config.ground.horizonZ || -200; // Odległość od kamery
        const sunX = this.config.sun.positionX * 100;
        const sunY = this.config.sun.positionY * 100;
        
        // Zapisz względną pozycję (będzie aktualizowana w animacji)
        this.sunRelativePosition = { x: sunX, y: sunY, z: sunDistance };
        this.updateSunPosition();
        
        this.scene.add(this.sun);
    }

    /**
     * Aktualizacja pozycji słońca względem kamery
     */
    updateSunPosition() {
        if (this.sun && this.sunRelativePosition) {
            // Słońce w stałej pozycji względem kamery
            this.sun.position.set(
                this.sunRelativePosition.x,
                this.sunRelativePosition.y,
                this.cameraZ + this.sunRelativePosition.z
            );
            
        }
    }
    
    /**
     * Animacja sceny
     */
    animate() {
        this.animationId = requestAnimationFrame(() => this.animate());
        
        // Aktualizuj Stats (FPS, czas renderowania)
        if (this.stats) {
            this.stats.begin();
        }
        
        // Oblicz FPS przed adaptive quality (potrzebne do decyzji o jakości)
        if (this.adaptiveQuality.enabled) {
            this.adaptiveQuality.calculateFPS(); // Aktualizuj FPS
            if (this.adaptiveQuality.update()) {
                this.updateRendererResolution();
            }
        }
        
        // Przesuń kamerę do przodu (w kierunku drogi)
        // Zmniejszamy z, bo droga jest w ujemnych wartościach z (np. -200)
        this.cameraZ -= this.config.camera.speed;
        this.camera.position.z = this.cameraZ;
        
        // Aktualizuj pozycję słońca względem kamery
        this.updateSunPosition();
        
        // Aktualizacja drogi - zarządza segmentami (kamera się przesuwa)
        if (this.road) {
            this.road.update(this.cameraZ);
        }

        // Aktualizacja gór - przebudowa siatki co segmentSpacing ruchu kamery (scroll)
        if (this.mountains) {
            this.mountains.update(this.cameraZ);
        }
        
        // Renderowanie z post-processing (bloom) lub bez
        if (this.postProcessing) {
            // WebGPU post-processing
            this.postProcessing.render();
        } else if (this.composer) {
            // WebGL post-processing
            this.renderer.clear(); // Ręczne czyszczenie przed renderowaniem
            this.composer.render();
        } else {
            // Bez post-processing
            this.renderer.render(this.scene, this.camera);
        }
        
        // Zakończ pomiar Stats
        if (this.stats) {
            this.stats.end();
        }
    }
    
    /**
     * Aktualizuj rozdzielczość renderera na podstawie aktualnego poziomu jakości
     */
    updateRendererResolution() {
        const adaptive = this.adaptiveQuality;
        const renderConfig = this.config.render || {};
        const currentResolutionScale = adaptive.currentResolutionScale;
        
        const devicePixelRatio = window.devicePixelRatio || 1.0;
        const targetPixelRatio = renderConfig.pixelRatio !== null && renderConfig.pixelRatio !== undefined
            ? renderConfig.pixelRatio 
            : Math.min(devicePixelRatio, 2.0);
        
        const viewportWidth = window.visualViewport?.width || window.innerWidth;
        const viewportHeight = window.visualViewport?.height || window.innerHeight;
        
        const targetWidth = Math.floor(viewportWidth * currentResolutionScale);
        const targetHeight = Math.floor(viewportHeight * currentResolutionScale);
        
        this.renderer.setSize(targetWidth, targetHeight);
        this.renderer.setPixelRatio(targetPixelRatio);
        
        if (this.composer) {
            this.composer.setSize(targetWidth, targetHeight);
            updateFXAAResolution(this.composer, targetWidth, targetHeight, targetPixelRatio);
        }
        
        const aspect = viewportWidth / viewportHeight;
        this.camera.aspect = aspect;
        this.camera.updateProjectionMatrix();
        
        const canvas = this.renderer.domElement;
        canvas.style.width = `${viewportWidth}px`;
        canvas.style.height = `${viewportHeight}px`;
        
        console.log(`[Adaptive Quality] Rozdzielczość zaktualizowana: ${targetWidth}x${targetHeight} @ ${targetPixelRatio}x (scale: ${currentResolutionScale}, viewport: ${viewportWidth}x${viewportHeight})`);
    }

    /**
     * Obsługa zmiany rozmiaru okna
     */
    onWindowResize() {
        // Aktualizuj rozdzielczość dla LineMaterial (jeśli używamy alt renderer)
        if (this.road && this.road.setResolution) {
            const viewportWidth = window.visualViewport?.width || window.innerWidth;
            const viewportHeight = window.visualViewport?.height || window.innerHeight;
            this.road.setResolution(viewportWidth, viewportHeight);
        }
        
        const renderConfig = this.config.render || {};
        const resolutionScale = renderConfig.resolutionScale || 1.0;
        const devicePixelRatio = window.devicePixelRatio || 1.0;
        const targetPixelRatio = renderConfig.pixelRatio !== null && renderConfig.pixelRatio !== undefined
            ? renderConfig.pixelRatio 
            : Math.min(devicePixelRatio, 2.0);
        
        // Użyj visualViewport jeśli dostępne (lepsze dla mobile), w przeciwnym razie window
        const viewportWidth = window.visualViewport?.width || window.innerWidth;
        const viewportHeight = window.visualViewport?.height || window.innerHeight;
        
        const targetWidth = Math.floor(viewportWidth * resolutionScale);
        const targetHeight = Math.floor(viewportHeight * resolutionScale);
        
        // Aktualizuj aspect ratio kamery
        const aspect = viewportWidth / viewportHeight;
        this.camera.aspect = aspect;
        this.camera.updateProjectionMatrix();
        
        // Aktualizuj rozmiar renderera
        this.renderer.setSize(targetWidth, targetHeight);
        this.renderer.setPixelRatio(targetPixelRatio);
        
        // Aktualizuj rozmiar canvas CSS (zapobiega rozciąganiu)
        const canvas = this.renderer.domElement;
        canvas.style.width = `${viewportWidth}px`;
        canvas.style.height = `${viewportHeight}px`;
        
        // Aktualizuj rozmiar EffectComposer jeśli istnieje
        if (this.composer) {
            this.composer.setSize(targetWidth, targetHeight);
            
            updateFXAAResolution(this.composer, targetWidth, targetHeight, targetPixelRatio);
        }
    }

    /**
     * Aktualizacja konfiguracji w runtime
     */
    updateConfig(newConfig) {
        this.config = this.mergeConfig(newConfig);
        
        // Aktualizuj kamerę
        if (this.camera) {
            this.camera.fov = this.config.camera.fov;
            this.camera.updateProjectionMatrix();
        }
        
        // Aktualizuj słońce
        if (this.sun) {
            this.sun.scale.setScalar(this.config.sun.size / 80); // 80 to domyślny rozmiar
            if (this.sun.material) {
                this.sun.material.color.setHex(this.config.sun.color);
                this.sun.material.emissive.setHex(this.config.sun.color);
            }
            // Aktualizuj pozycję względną
            if (this.sunRelativePosition) {
                this.sunRelativePosition.x = this.config.sun.positionX;
                this.sunRelativePosition.y = this.config.sun.positionY;
            }
        }
        
        // Aktualizuj bloom
        if (this.config.ground?.bloom) {
            const bloomConfig = this.config.ground.bloom;
            
            // WebGPU bloom (TSL)
            // Sprawdź czy renderer to WebGPU (bez importu - używamy sprawdzenia typu)
            const isWebGPURenderer = this.useWebGPU && this.renderer && 
                this.renderer.constructor.name === 'WebGPURenderer';
            
            if (this.postProcessing && isWebGPURenderer && this.scenePassColor && this.bloomModule) {
                // Jeśli bloom ma być włączony
                if (bloomConfig.enabled) {
                    // Jeśli bloomPass nie istnieje, utwórz go
                    if (!this.bloomPass) {
                        this.bloomPass = this.bloomModule.bloom(this.scenePassColor);
                        this.bloomPass.threshold.value = bloomConfig.threshold || 0.1;
                        this.bloomPass.strength.value = bloomConfig.strength || 0.5;
                        this.bloomPass.radius.value = bloomConfig.radius || 0.4;
                    } else {
                        // Aktualizuj parametry bloom
                        if (bloomConfig.threshold !== undefined) {
                            this.bloomPass.threshold.value = bloomConfig.threshold;
                        }
                        if (bloomConfig.strength !== undefined) {
                            this.bloomPass.strength.value = bloomConfig.strength;
                        }
                        if (bloomConfig.radius !== undefined) {
                            this.bloomPass.radius.value = bloomConfig.radius;
                        }
                    }
                    
                    // Połącz scenę z bloom
                    this.postProcessing.outputNode = this.scenePassColor.add(this.bloomPass);
                } else {
                    // Wyłącz bloom - tylko scena
                    this.postProcessing.outputNode = this.scenePassColor;
                }
            }
            
            // WebGL bloom (EffectComposer)
            if (this.composer) {
                const bloomPass = this.composer.passes.find(pass => pass instanceof UnrealBloomPass);
                if (bloomPass) {
                    bloomPass.enabled = bloomConfig.enabled;
                    if (bloomConfig.threshold !== undefined) {
                        bloomPass.threshold = bloomConfig.threshold;
                    }
                    if (bloomConfig.strength !== undefined) {
                        bloomPass.strength = bloomConfig.strength;
                    }
                    if (bloomConfig.radius !== undefined) {
                        bloomPass.radius = bloomConfig.radius;
                    }
                }
            }
        }
        
        // Aktualizuj rozdzielczość
        if (this.renderer && this.config.render) {
            const renderConfig = this.config.render;
            const adaptiveQuality = this.adaptiveQuality;
            const baseResolutionScale = renderConfig.resolutionScale || 1.0;
            
            if (this.adaptiveQuality.enabled) {
                this.adaptiveQuality.baseResolutionScale = baseResolutionScale;
                this.adaptiveQuality.currentResolutionScale = baseResolutionScale;
            }
            
            const currentResolutionScale = this.adaptiveQuality.enabled 
                ? this.adaptiveQuality.currentResolutionScale
                : baseResolutionScale;
            
            const devicePixelRatio = window.devicePixelRatio || 1.0;
            const targetPixelRatio = renderConfig.pixelRatio !== null && renderConfig.pixelRatio !== undefined
                ? renderConfig.pixelRatio 
                : Math.min(devicePixelRatio, 2.0);
            
            // Użyj visualViewport jeśli dostępne (lepsze dla mobile), w przeciwnym razie window
            const viewportWidth = window.visualViewport?.width || window.innerWidth;
            const viewportHeight = window.visualViewport?.height || window.innerHeight;
            
            const targetWidth = Math.floor(viewportWidth * currentResolutionScale);
            const targetHeight = Math.floor(viewportHeight * currentResolutionScale);
            
            this.renderer.setSize(targetWidth, targetHeight);
            this.renderer.setPixelRatio(targetPixelRatio);
            
            if (this.composer) {
                this.composer.setSize(targetWidth, targetHeight);
            }
            
            // Aktualizuj aspect ratio kamery
            const aspect = viewportWidth / viewportHeight;
            this.camera.aspect = aspect;
            this.camera.updateProjectionMatrix();
            
            // Aktualizuj rozmiar canvas CSS (zapobiega rozciąganiu)
            const canvas = this.renderer.domElement;
            canvas.style.width = `${viewportWidth}px`;
            canvas.style.height = `${viewportHeight}px`;
        }
        
        // Aktualizuj drogę
        if (this.road) {
            this.road.updateConfig(this.config.ground);
        }

        // Aktualizuj góry
        if (this.mountains && this.config.mountains) {
            this.mountains.updateConfig(this.config.mountains);
        }
    }

    /**
     * Cleanup resources
     */
    dispose() {
        if (this.animationId) {
            cancelAnimationFrame(this.animationId);
        }
        
        // Usuń Stats
        if (this.stats && this.stats.dom && this.stats.dom.parentNode) {
            this.stats.dom.parentNode.removeChild(this.stats.dom);
            this.stats = null;
        }
        
        // Cleanup road
        if (this.road) {
            this.road.dispose();
            const roadGroup = this.road.group;
            if (roadGroup) {
                this.scene.remove(roadGroup);
            }
        }

        // Cleanup mountains
        if (this.mountains) {
            this.mountains.dispose();
            if (this.mountains.group) {
                this.scene.remove(this.mountains.group);
            }
            this.mountains = null;
        }
        
        if (this.renderer) {
            this.renderer.dispose();
            if (this.renderer.domElement && this.renderer.domElement.parentNode) {
                this.renderer.domElement.parentNode.removeChild(this.renderer.domElement);
            }
        }
        
        window.removeEventListener('resize', () => this.onWindowResize());
    }
}

